/**
 * Timeline hover-card for the cross-product Alerts page — same per-alert line
 * rendering as AlertSummaryCard, but bucketed by product: one single-line
 * header (product thumbnail + "Name - City - Model", reusing ProductGroupCard's
 * `inline` variant — there's more horizontal room here than in the left group
 * panel, so unlike that panel this header is one line, sized to match the
 * alert rows' own height rather than stacking name/subtitle) with a divider
 * beneath it, then that product's alerts, per group. Groups preserve the
 * order their alerts first appear in `alerts` (already chronological — see
 * TimelineView's activeAtCursor, built from computeBars's openedMs-sorted output).
 */
import * as React from "react";
import type { ProductAlertEpisode, ProductAlertEpisodeWithProduct, ProductListItem } from "../../lib/api/products";
import { AlertSummaryCard } from "./AlertSummaryCard";
import { ProductGroupCard } from "./ProductGroupCard";
import * as s from "./GroupedAlertSummaryCard.css";

export function GroupedAlertSummaryCard({
  alerts,
  getProduct,
  accessToken,
  emptyLabel = "No alerts",
  asOf,
  getColor,
  highlightedId,
  selectedId,
}: {
  alerts: (ProductAlertEpisodeWithProduct & { id?: string })[];
  getProduct: (productId: string) => ProductListItem | undefined;
  accessToken: string | null;
  emptyLabel?: string;
  asOf?: number;
  getColor?: (alert: ProductAlertEpisode) => string;
  highlightedId?: string | null;
  selectedId?: string | null;
}) {
  const groups = React.useMemo(() => {
    const order: string[] = [];
    const byProduct = new Map<string, ProductAlertEpisodeWithProduct[]>();
    for (const alert of alerts) {
      if (!byProduct.has(alert.productId)) {
        byProduct.set(alert.productId, []);
        order.push(alert.productId);
      }
      byProduct.get(alert.productId)!.push(alert);
    }
    return order.map((productId) => ({ productId, alerts: byProduct.get(productId)! }));
  }, [alerts]);

  if (groups.length === 0) {
    return <div className={s.empty}>{emptyLabel}</div>;
  }

  return (
    <div className={s.groupList}>
      {groups.map(({ productId, alerts: groupAlerts }) => (
        <div key={productId}>
          <div className={s.groupHeader}>
            <ProductGroupCard
              productId={productId}
              product={getProduct(productId)}
              accessToken={accessToken}
              variant="inline"
            />
          </div>
          <div className={s.groupDivider} />
          <AlertSummaryCard
            alerts={groupAlerts}
            asOf={asOf}
            getColor={getColor}
            highlightedId={highlightedId}
            selectedId={selectedId}
          />
        </div>
      ))}
    </div>
  );
}
